<template>
  <div class="play-page">
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <desktop-brand-corner />

    <main class="page-shell">
      <div class="mobile-topbar" :class="{ 'is-scrolled': isTopbarScrolled }">
        <button
          type="button"
          class="mobile-back-btn"
          @click="handleGoBack"
          aria-label="返回上一页"
        >
          <font-awesome-icon icon="chevron-left" />
        </button>
        <h1 class="mobile-topbar-title">{{ mobileTopbarTitle }}</h1>
        <span class="mobile-topbar-spacer" aria-hidden="true"></span>
      </div>
      <div class="mobile-topbar-offset" aria-hidden="true"></div>

      <section ref="playerFrameRef" class="player-stage" :style="{ aspectRatio: playerAspectRatio }">
        <video
          ref="videoRef"
          class="player-video"
          :class="{ hidden: isPlayTimeLimitReached }"
          :src="activeEpisode.videoUrl"
          :muted="isMuted"
          preload="metadata"
          x-webkit-airplay="allow"
          airplay="allow"
          playsinline
          @click="togglePreviewPlay"
          @play="handleVideoPlay"
          @pause="handleVideoPause"
          @loadedmetadata="handleLoadedMetadata"
          @timeupdate="handleTimeUpdate"
          @ended="handleVideoEnded"
          @error="handleVideoError"
        ></video>

        <div v-if="isPlayTimeLimitReached" class="play-limit-cover">
          <div class="play-limit-card">
            <div class="play-limit-icon">
              <font-awesome-icon icon="moon" />
            </div>
            <p class="play-limit-text">小朋友，休息时间到了！</p>
            <button type="button" class="parent-unlock-btn" @click="handleParentUnlock">
              家长解锁
            </button>
          </div>
        </div>

        <div v-else-if="videoError" class="player-message">视频加载失败，请稍后重试</div>

        <div v-if="!isPlayTimeLimitReached" class="player-controls">
          <div class="progress-track" @click="seekByProgress">
            <span class="progress-fill" :style="{ width: `${previewProgress}%` }"></span>
          </div>

          <div class="controls-row">
            <button type="button" class="control-btn" @click="togglePreviewPlay">
              <font-awesome-icon :icon="isPreviewPlaying ? 'pause' : 'play'" />
            </button>
            <span class="time-text">
              {{ formatTime(currentSecond) }} / {{ formatTime(videoDuration) }}
            </span>

            <span class="controls-spacer"></span>

            <button type="button" class="control-btn" @click="toggleMute">
              <font-awesome-icon :icon="isMuted ? 'volume-xmark' : 'volume-high'" />
            </button>
            <button
              v-if="showCastControl"
              type="button"
              class="control-btn"
              :class="{ active: isCasting }"
              :title="castButtonTitle"
              @click="openCastPicker"
            >
              <font-awesome-icon icon="tv" />
            </button>
            <span v-if="showCastControl && (isCastConnecting || isCasting)" class="cast-state">
              {{ isCastConnecting ? '连接中' : '投屏中' }}
            </span>
            <button type="button" class="control-btn" @click="toggleFullscreen">
              <font-awesome-icon :icon="isFullscreen ? 'compress' : 'expand'" />
            </button>
          </div>
        </div>
      </section>

      <header class="meta-header">
        <section class="content-section">
          <div class="content-title-row">
            <h2 class="section-title">简介</h2>

            <button
              type="button"
              class="favorite-season-btn"
              :class="{ active: isSeasonFavorite }"
              @click="toggleSeasonFavorite"
            >
              <span class="favorite-season-icon">
                <font-awesome-icon icon="heart" />
              </span>
              <span class="favorite-season-text">收藏</span>
            </button>
          </div>
          <p class="series-desc">{{ seriesInfo.description }}</p>
        </section>
      </header>

      <section class="season-tabs-section">
        <div ref="seasonChipsRef" class="season-tabs">
          <button
            v-for="season in seasons"
            :key="season.id"
            type="button"
            class="season-tab"
            :data-season-id="season.id"
            :class="{ active: activeSeason === season.id }"
            @click="selectSeason(season.id)"
          >
            {{ season.label }}
          </button>
        </div>

        <button type="button" class="menu-toggle-btn" @click="toggleSeasonSheet">
          <font-awesome-icon icon="chevron-down" :class="{ expanded: isSeasonSheetOpen }" />
        </button>
      </section>

      <section class="episode-panel">
        <div class="episode-panel-head">
          <h2 class="episode-panel-title">选集</h2>

          <button type="button" class="episode-count-btn" @click="toggleEpisodeRangeSheet">
            <span>{{ filteredEpisodeCountText }}</span>
            <font-awesome-icon icon="chevron-right" />
          </button>
        </div>

        <section class="episode-range-section">
          <div ref="episodeRangeChipsRef" class="episode-range-chips">
            <button
              v-for="range in episodeRanges"
              :key="range.id"
              type="button"
              class="episode-range-chip"
              :data-range-id="range.id"
              :class="{ active: activeEpisodeRange === range.id }"
              @click="selectEpisodeRange(range.id)"
            >
              {{ range.label }}
            </button>
          </div>
        </section>

        <section ref="episodeGridRef" class="episode-grid">
          <article
            v-for="episode in visibleEpisodes"
            :key="episode.id"
            class="episode-card"
            :data-episode-id="episode.id"
            :class="{ active: activeEpisodeId === episode.id }"
            @click="selectEpisode(episode.id)"
          >
          <div
            class="episode-cover"
            :style="{ '--cover-start': episode.coverStart, '--cover-end': episode.coverEnd }"
          >
            <font-awesome-icon icon="image" class="cover-icon" />
            <span class="episode-tag" :class="episode.status">
              {{ episode.status === 'learned' ? `已学${episode.learnCount}遍` : '未学' }}
            </span>
          </div>

          <h3 class="episode-title">{{ episode.title }}</h3>
          <p class="episode-subtitle">{{ episode.subtitle }}</p>
        </article>
        </section>
      </section>
    </main>

    <Transition name="sheet-fade">
      <div v-if="isSeasonSheetOpen" class="sheet-mask" @click="closeSeasonSheet">
        <section class="sheet-panel" @click.stop>
          <h3 class="sheet-title">选择季数</h3>

          <button
            v-for="season in seasons"
            :key="season.id"
            type="button"
            class="sheet-option"
            :class="{ active: activeSeason === season.id }"
            @click="selectSeasonFromSheet(season.id)"
          >
            <span>{{ season.label }}</span>
            <span v-if="activeSeason === season.id" class="sheet-check">已选</span>
          </button>

          <button type="button" class="sheet-cancel" @click="closeSeasonSheet">取消</button>
        </section>
      </div>
    </Transition>

    <Transition name="sheet-fade">
      <div v-if="isEpisodeRangeSheetOpen" class="sheet-mask" @click="closeEpisodeRangeSheet">
        <section class="sheet-panel" @click.stop>
          <h3 class="sheet-title">选择集数</h3>

          <button
            v-for="episode in filteredEpisodes"
            :key="episode.id"
            type="button"
            class="sheet-option"
            :class="{ active: activeEpisodeId === episode.id }"
            @click="selectEpisodeFromSheet(episode.id)"
          >
            <span>{{ episode.title }} {{ episode.subtitle }}</span>
            <span v-if="activeEpisodeId === episode.id" class="sheet-check">已选</span>
          </button>

          <button type="button" class="sheet-cancel" @click="closeEpisodeRangeSheet">取消</button>
        </section>
      </div>
    </Transition>

    <Transition name="modal-fade">
      <div
        v-if="isParentUnlockModalOpen"
        class="parent-modal-mask"
        @click="closeParentUnlockModal"
      >
        <section class="parent-modal" @click.stop>
          <button
            type="button"
            class="parent-modal-close"
            aria-label="关闭"
            @click="closeParentUnlockModal"
          >
            <font-awesome-icon icon="xmark" />
          </button>

          <header class="parent-modal-header">
            <h2 class="parent-modal-title">家长模式</h2>
            <p class="parent-modal-subtitle">请输入家长密码后继续</p>
          </header>

          <form class="parent-modal-body" @submit.prevent="submitParentUnlockPassword">
            <div class="parent-lookup-block">
              <label class="parent-lookup-label" for="parent-unlock-password-input">密码</label>
              <div class="parent-input-shell">
                <input
                  id="parent-unlock-password-input"
                  ref="parentPasswordInputRef"
                  v-model="parentPassword"
                  :type="showParentPassword ? 'text' : 'password'"
                  placeholder="请输入密码"
                  class="parent-input"
                  autocomplete="current-password"
                  @input="parentPasswordError = ''"
                />
                <button
                  type="button"
                  class="parent-input-side-btn"
                  :aria-label="showParentPassword ? '隐藏密码' : '显示密码'"
                  :title="showParentPassword ? '隐藏密码' : '显示密码'"
                  @click="showParentPassword = !showParentPassword"
                >
                  <font-awesome-icon :icon="showParentPassword ? 'eye-slash' : 'eye'" />
                </button>
              </div>
              <p v-if="parentPasswordError" class="parent-lookup-error" role="alert">
                {{ parentPasswordError }}
              </p>
            </div>

            <button
              type="submit"
              class="parent-submit-btn"
              :class="{ active: canSubmitParentPassword }"
              :disabled="!canSubmitParentPassword"
            >
              继续观看
            </button>
          </form>
        </section>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const PARENT_MODE_PASSWORD = '123456'

const seriesInfo = {
  description:
    'Yakka Dee（亚克迪）是一部CBBC的儿童电视节目，适合零基础或者刚接触英语的孩子。通过卡通人物和真人孩子的互动，鼓励孩子们开口说话。',
}

const seasons = [
  { id: 's1', label: '第一季' },
  { id: 's2', label: '第二季' },
  { id: 's3', label: '第三季' },
  { id: 's4', label: '第四季' },
  { id: 's5', label: '第五季' },
  { id: 's6', label: '第六季' },
]

const seasonEpisodeCounts = {
  s1: 30,
  s2: 24,
  s3: 18,
  s4: 30,
  s5: 20,
  s6: 15,
}

const episodeWords = [
  'Banana',
  'Apple',
  'Bus',
  'Dog',
  'Orange',
  'Water',
  'Cat',
  'Duck',
  'Car',
  'Ball',
  'Book',
  'Milk',
  'Shoes',
  'Train',
  'Flower',
  'Star',
  'Fish',
  'Cookie',
  'Happy',
  'Jump',
  'Yellow',
  'Blue',
  'Rain',
  'Sun',
  'Moon',
  'Tree',
  'Baby',
  'Music',
  'Dance',
  'Smile',
]
const bananaVideoUrl = 'https://raw.githubusercontent.com/chinaitman/ws-chat-room/main/banana.mp4'
const coverPalette = [
  ['#d3d3d3', '#cfcfcf'],
  ['#d8d8d8', '#d2d2d2'],
  ['#d6d6d6', '#cecece'],
  ['#d5d5d5', '#cdcdcd'],
  ['#d9d9d9', '#d1d1d1'],
  ['#d4d4d4', '#cccccc'],
]

const episodes = seasons.flatMap((season) =>
  Array.from({ length: seasonEpisodeCounts[season.id] ?? episodeWords.length }, (_, index) => {
    const word = episodeWords[index % episodeWords.length]
    const [coverStart, coverEnd] = coverPalette[index % coverPalette.length]
    const learned = index === 0 || index === 3

    return {
      id: `${season.id}-${index + 1}`,
      seasonId: season.id,
      title: `yakka dee ${season.label} ${String(index + 1).padStart(2, '0')}`,
      subtitle: word,
      status: learned ? 'learned' : 'unlearned',
      learnCount: learned ? 1 : 0,
      videoUrl: bananaVideoUrl,
      coverStart,
      coverEnd,
    }
  })
)

const activeSeason = ref(seasons[0].id)
const activeEpisodeId = ref(episodes[0].id)
const activeEpisodeRange = ref('')
const isSeasonSheetOpen = ref(false)
const isEpisodeRangeSheetOpen = ref(false)
const favoriteSeasonIds = ref([])
const seasonChipsRef = ref(null)
const episodeRangeChipsRef = ref(null)
const episodeGridRef = ref(null)
const playerFrameRef = ref(null)
const videoRef = ref(null)

const isPreviewPlaying = ref(false)
const isMuted = ref(false)
const isFullscreen = ref(false)
const currentSecond = ref(0)
const videoDuration = ref(0)
const videoError = ref(false)
const playerAspectRatio = ref('16 / 9')
const isRemotePlaybackSupported = ref(false)
const isAirPlaySupported = ref(false)
const isCasting = ref(false)
const isCastConnecting = ref(false)
const isMobileDevice = ref(false)
const isTopbarScrolled = ref(false)
const dailyPlayLimitMinutes = ref(3)
const watchedTodaySeconds = ref(2 * 60)
const isParentUnlocked = ref(false)
const isParentUnlockModalOpen = ref(false)
const parentPassword = ref('')
const parentPasswordError = ref('')
const showParentPassword = ref(false)
const parentPasswordInputRef = ref(null)
let lastTrackedVideoSecond = 0
let remotePlaybackRef = null

const filteredEpisodes = computed(() =>
  episodes.filter((episode) => episode.seasonId === activeSeason.value)
)

const episodeRanges = computed(() => {
  const groupSize = 15
  const list = filteredEpisodes.value

  return Array.from({ length: Math.ceil(list.length / groupSize) }, (_, index) => {
    const start = index * groupSize
    const items = list.slice(start, start + groupSize)
    const first = start + 1
    const last = start + items.length

    return {
      id: `range-${index + 1}`,
      label: `${first}-${last}`,
      items,
    }
  })
})

const visibleEpisodes = computed(() => {
  const activeRange = episodeRanges.value.find((range) => range.id === activeEpisodeRange.value)
  return activeRange ? activeRange.items : filteredEpisodes.value
})

const filteredEpisodeCountText = computed(() => `共${filteredEpisodes.value.length}集`)

const canCast = computed(
  () => isRemotePlaybackSupported.value || isAirPlaySupported.value
)

const showCastControl = computed(() => isMobileDevice.value && canCast.value)

const dailyPlayLimitSeconds = computed(() => dailyPlayLimitMinutes.value * 60)

const canSubmitParentPassword = computed(() => parentPassword.value.trim().length > 0)

const isPlayTimeLimitReached = computed(() =>
  watchedTodaySeconds.value >= dailyPlayLimitSeconds.value && !isParentUnlocked.value
)

const castButtonTitle = computed(() => {
  if (!canCast.value) {
    return '当前设备不支持投屏'
  }

  if (isCastConnecting.value) {
    return '投屏设备连接中'
  }

  if (isCasting.value) {
    return '已连接投屏设备'
  }

  return '投屏到电视'
})

const activeEpisode = computed(() => {
  const selected = episodes.find((episode) => episode.id === activeEpisodeId.value)
  if (selected) {
    return selected
  }

  return filteredEpisodes.value[0] || episodes[0]
})

const isSeasonFavorite = computed(() =>
  favoriteSeasonIds.value.includes(activeSeason.value)
)

const mobileTopbarTitle = computed(() => {
  if (!activeEpisode.value) {
    return ''
  }

  return [activeEpisode.value.title, activeEpisode.value.subtitle].filter(Boolean).join(' ')
})

const previewProgress = computed(() => {
  const duration = videoDuration.value
  if (!duration) {
    return 0
  }

  return Math.min((currentSecond.value / duration) * 100, 100)
})

const formatTime = (seconds) => {
  const total = Math.max(0, Math.floor(seconds))
  const min = Math.floor(total / 60)
  const sec = String(total % 60).padStart(2, '0')
  return `${min}:${sec}`
}

const handleLoadedMetadata = () => {
  const video = videoRef.value
  if (!video) {
    return
  }

  videoDuration.value = Number.isFinite(video.duration) ? video.duration : 0
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    playerAspectRatio.value = `${video.videoWidth} / ${video.videoHeight}`
  }
}

const handleTimeUpdate = () => {
  const video = videoRef.value
  if (!video) {
    return
  }

  if (isPlayTimeLimitReached.value) {
    video.pause()
    return
  }

  const previousSecond = lastTrackedVideoSecond || video.currentTime
  const watchedDelta = Math.max(0, video.currentTime - previousSecond)
  watchedTodaySeconds.value = Math.min(
    dailyPlayLimitSeconds.value,
    watchedTodaySeconds.value + watchedDelta
  )
  lastTrackedVideoSecond = video.currentTime
  currentSecond.value = video.currentTime

  if (isPlayTimeLimitReached.value) {
    video.pause()
  }
}

const handleVideoPlay = () => {
  if (isPlayTimeLimitReached.value) {
    videoRef.value?.pause()
    return
  }

  lastTrackedVideoSecond = videoRef.value?.currentTime || 0
  isPreviewPlaying.value = true
}

const handleVideoPause = () => {
  isPreviewPlaying.value = false
}

const handleVideoEnded = () => {
  isPreviewPlaying.value = false
}

const handleVideoError = () => {
  videoError.value = true
  isPreviewPlaying.value = false
}

const handleRemotePlaybackStateChange = () => {
  const video = videoRef.value
  if (!video || !video.remote) {
    return
  }

  const state = video.remote.state || 'disconnected'
  isCastConnecting.value = state === 'connecting'
  isCasting.value = state === 'connected'
}

const handleAirPlayStateChange = () => {
  const video = videoRef.value
  if (!video) {
    return
  }

  const wireless = Boolean(video.webkitCurrentPlaybackTargetIsWireless)
  if (wireless) {
    isCasting.value = true
    isCastConnecting.value = false
    return
  }

  if (!isRemotePlaybackSupported.value) {
    isCasting.value = false
    isCastConnecting.value = false
  }
}

const initCastSupport = () => {
  const video = videoRef.value
  if (!video) {
    return
  }

  isRemotePlaybackSupported.value = Boolean(
    video.remote && typeof video.remote.prompt === 'function'
  )
  isAirPlaySupported.value = typeof video.webkitShowPlaybackTargetPicker === 'function'

  if (isRemotePlaybackSupported.value) {
    handleRemotePlaybackStateChange()
  }

  if (isAirPlaySupported.value) {
    handleAirPlayStateChange()
  }
}

const detectMobileDevice = () => {
  const ua = navigator.userAgent || ''
  const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone|Opera Mini|IEMobile/i.test(ua)
  const touchLike = navigator.maxTouchPoints > 1
  const coarsePointer = window.matchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false

  isMobileDevice.value = uaMobile || (touchLike && coarsePointer)
}

const bindCastEvents = () => {
  const video = videoRef.value
  if (!video) {
    return
  }

  if (video.remote && typeof video.remote.addEventListener === 'function') {
    remotePlaybackRef = video.remote
    remotePlaybackRef.addEventListener('connecting', handleRemotePlaybackStateChange)
    remotePlaybackRef.addEventListener('connect', handleRemotePlaybackStateChange)
    remotePlaybackRef.addEventListener('disconnect', handleRemotePlaybackStateChange)
  }

  video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', handleAirPlayStateChange)
}

const unbindCastEvents = () => {
  if (remotePlaybackRef) {
    remotePlaybackRef.removeEventListener('connecting', handleRemotePlaybackStateChange)
    remotePlaybackRef.removeEventListener('connect', handleRemotePlaybackStateChange)
    remotePlaybackRef.removeEventListener('disconnect', handleRemotePlaybackStateChange)
    remotePlaybackRef = null
  }

  if (videoRef.value) {
    videoRef.value.removeEventListener(
      'webkitcurrentplaybacktargetiswirelesschanged',
      handleAirPlayStateChange
    )
  }
}

const openCastPicker = async () => {
  const video = videoRef.value
  if (!video || !canCast.value) {
    return
  }

  if (isAirPlaySupported.value && typeof video.webkitShowPlaybackTargetPicker === 'function') {
    isCastConnecting.value = true
    video.webkitShowPlaybackTargetPicker()

    window.setTimeout(() => {
      if (!isCasting.value) {
        isCastConnecting.value = false
      }
    }, 3000)
    return
  }

  if (isRemotePlaybackSupported.value && video.remote && typeof video.remote.prompt === 'function') {
    try {
      await video.remote.prompt()
      handleRemotePlaybackStateChange()
    } catch {
      isCastConnecting.value = false
    }
  }
}

const togglePreviewPlay = async () => {
  const video = videoRef.value
  if (!video || videoError.value || isPlayTimeLimitReached.value) {
    return
  }

  try {
    if (video.paused || video.ended) {
      await video.play()
      return
    }

    video.pause()
  } catch {
    isPreviewPlaying.value = false
  }
}

const resetPreview = () => {
  const video = videoRef.value
  if (video) {
    video.pause()
    video.currentTime = 0
  }

  isPreviewPlaying.value = false
  currentSecond.value = 0
  lastTrackedVideoSecond = 0
  videoError.value = false
}

const toggleMute = () => {
  isMuted.value = !isMuted.value
  if (videoRef.value) {
    videoRef.value.muted = isMuted.value
  }
}

const seekByProgress = (event) => {
  const video = videoRef.value
  if (isPlayTimeLimitReached.value) {
    return
  }

  if (!video || !videoDuration.value) {
    return
  }

  const rect = event.currentTarget.getBoundingClientRect()
  const ratio = (event.clientX - rect.left) / rect.width
  const progress = Math.min(Math.max(ratio, 0), 1)
  video.currentTime = progress * videoDuration.value
  currentSecond.value = video.currentTime
  lastTrackedVideoSecond = video.currentTime
}

const openParentUnlockModal = () => {
  parentPassword.value = ''
  parentPasswordError.value = ''
  showParentPassword.value = false
  isParentUnlockModalOpen.value = true

  nextTick(() => {
    parentPasswordInputRef.value?.focus()
  })
}

const closeParentUnlockModal = () => {
  isParentUnlockModalOpen.value = false
  parentPassword.value = ''
  parentPasswordError.value = ''
  showParentPassword.value = false
}

const handleParentUnlock = () => {
  openParentUnlockModal()
}

const submitParentUnlockPassword = async () => {
  if (!canSubmitParentPassword.value) {
    parentPasswordError.value = '请输入密码'
    return
  }

  if (parentPassword.value !== PARENT_MODE_PASSWORD) {
    parentPasswordError.value = '密码不正确，请重新输入'
    return
  }

  isParentUnlocked.value = true
  closeParentUnlockModal()
  await nextTick()

  const video = videoRef.value
  if (video) {
    lastTrackedVideoSecond = video.currentTime
    try {
      await video.play()
    } catch {
      isPreviewPlaying.value = false
    }
  }
}

const handleFullscreenChange = () => {
  isFullscreen.value = document.fullscreenElement === playerFrameRef.value
}

const toggleFullscreen = async () => {
  const frame = playerFrameRef.value
  if (!frame) {
    return
  }

  try {
    if (!document.fullscreenElement) {
      await frame.requestFullscreen()
      return
    }

    await document.exitFullscreen()
  } catch {
    isFullscreen.value = false
  }
}

const syncSeasonChipIntoView = () => {
  nextTick(() => {
    const chipsContainer = seasonChipsRef.value
    if (!chipsContainer) {
      return
    }

    const activeChip = chipsContainer.querySelector(
      `button[data-season-id="${activeSeason.value}"]`
    )
    if (!activeChip) {
      return
    }

    activeChip.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}

const syncActiveEpisodeRange = () => {
  const ranges = episodeRanges.value
  if (!ranges.length) {
    activeEpisodeRange.value = ''
    return
  }

  const matchedRange = ranges.find((range) =>
    range.items.some((episode) => episode.id === activeEpisodeId.value)
  )

  activeEpisodeRange.value = matchedRange ? matchedRange.id : ranges[0].id
}

const syncEpisodeRangeChipIntoView = () => {
  nextTick(() => {
    const chipsContainer = episodeRangeChipsRef.value
    if (!chipsContainer) {
      return
    }

    const activeChip = chipsContainer.querySelector(
      `button[data-range-id="${activeEpisodeRange.value}"]`
    )
    if (!activeChip) {
      return
    }

    activeChip.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}

const syncEpisodeCardIntoView = () => {
  nextTick(() => {
    const grid = episodeGridRef.value
    if (!grid) {
      return
    }

    const activeCard = grid.querySelector(`article[data-episode-id="${activeEpisodeId.value}"]`)
    if (!activeCard) {
      return
    }

    activeCard.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}

const syncRouteQuery = () => {
  router
    .replace({
      path: '/play',
      query: {
        season: activeSeason.value,
        episode: activeEpisodeId.value,
      },
    })
    .catch(() => {})
}

const ensureEpisodeInSeason = () => {
  const current = episodes.find((episode) => episode.id === activeEpisodeId.value)
  if (current && current.seasonId === activeSeason.value) {
    return
  }

  const firstEpisode = filteredEpisodes.value[0]
  if (!firstEpisode) {
    return
  }

  activeEpisodeId.value = firstEpisode.id
}

const selectSeason = (seasonId) => {
  activeSeason.value = seasonId
  ensureEpisodeInSeason()
  syncActiveEpisodeRange()
  resetPreview()
  syncSeasonChipIntoView()
  syncEpisodeRangeChipIntoView()
  syncEpisodeCardIntoView()
  syncRouteQuery()
}

const selectEpisode = (episodeId) => {
  const target = episodes.find((episode) => episode.id === episodeId)
  if (!target) {
    return
  }

  activeSeason.value = target.seasonId
  activeEpisodeId.value = target.id
  syncActiveEpisodeRange()
  resetPreview()
  syncSeasonChipIntoView()
  syncEpisodeRangeChipIntoView()
  syncEpisodeCardIntoView()
  syncRouteQuery()
}

const selectEpisodeRange = (rangeId) => {
  activeEpisodeRange.value = rangeId
  syncEpisodeRangeChipIntoView()
  syncEpisodeCardIntoView()
}

const toggleSeasonSheet = () => {
  isEpisodeRangeSheetOpen.value = false
  isSeasonSheetOpen.value = !isSeasonSheetOpen.value
}

const closeSeasonSheet = () => {
  isSeasonSheetOpen.value = false
}

const selectSeasonFromSheet = (seasonId) => {
  selectSeason(seasonId)
  closeSeasonSheet()
}

const toggleSeasonFavorite = () => {
  if (isSeasonFavorite.value) {
    favoriteSeasonIds.value = favoriteSeasonIds.value.filter((id) => id !== activeSeason.value)
    return
  }

  favoriteSeasonIds.value = [...favoriteSeasonIds.value, activeSeason.value]
}

const toggleEpisodeRangeSheet = () => {
  isSeasonSheetOpen.value = false
  isEpisodeRangeSheetOpen.value = !isEpisodeRangeSheetOpen.value
}

const closeEpisodeRangeSheet = () => {
  isEpisodeRangeSheetOpen.value = false
}

const selectEpisodeFromSheet = (episodeId) => {
  selectEpisode(episodeId)
  closeEpisodeRangeSheet()
}

const applyRouteQuery = () => {
  const seasonQuery = typeof route.query.season === 'string' ? route.query.season : ''
  const episodeQuery = typeof route.query.episode === 'string' ? route.query.episode : ''

  if (seasons.some((season) => season.id === seasonQuery)) {
    activeSeason.value = seasonQuery
  }

  if (episodes.some((episode) => episode.id === episodeQuery)) {
    const selected = episodes.find((episode) => episode.id === episodeQuery)
    activeEpisodeId.value = episodeQuery
    activeSeason.value = selected ? selected.seasonId : activeSeason.value
  }

  ensureEpisodeInSeason()
  syncActiveEpisodeRange()
}

const scrollPageToTop = () => {
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/videos')
}

const handlePageScroll = () => {
  isTopbarScrolled.value = window.scrollY > 12
}

onMounted(() => {
  scrollPageToTop()
  applyRouteQuery()
  syncRouteQuery()
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
  detectMobileDevice()

  if (isMobileDevice.value) {
    initCastSupport()
    bindCastEvents()
  }
})

onUnmounted(() => {
  if (videoRef.value) {
    videoRef.value.pause()
  }
  unbindCastEvents()
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  window.removeEventListener('scroll', handlePageScroll)
})
</script>

<style scoped>
.play-page {
  min-height: 100vh;
  background: #f9f7f2;
  position: relative;
  isolation: isolate;
  overflow-x: hidden;
  padding: 16px 0 100px;
}

.bg-gradient {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #fff8f0 0%, #f9f7f2 50%, #f5f0e8 100%);
  z-index: 0;
  pointer-events: none;
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.6;
  z-index: 0;
  animation: float 8s ease-in-out infinite;
  pointer-events: none;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #ffe4d4, #ffd4c4);
  top: -150px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #e8f4ff, #d4e8ff);
  bottom: -100px;
  left: -100px;
  animation-delay: -3s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #ffeaa8, #ffd88a);
  top: 50%;
  left: 30%;
  transform: translateY(-50%);
  animation-delay: -5s;
  opacity: 0.4;
}

@keyframes float {
  0%,
  100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.page-shell {
  width: min(1200px, calc(100% - 44px));
  margin: 0 auto;
  position: relative;
  z-index: 1;
  padding-top: 64px;
}

.mobile-topbar {
  display: none;
}

.mobile-topbar-offset {
  display: none;
}

.mobile-back-btn {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mobile-topbar-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0 12px;
  color: #2d3436;
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-topbar-spacer {
  display: none;
}

.player-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1.54 / 1;
  border-radius: 0;
  background: linear-gradient(180deg, #5f6064 0%, #5d5f63 54%, #0c1016 100%);
  overflow: hidden;
}

.player-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #12161d;
}

.player-video.hidden {
  visibility: hidden;
}

.player-stage::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 45%, rgba(10, 13, 18, 0.72) 90%);
  pointer-events: none;
}

.play-limit-cover {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(circle at 50% 35%, rgba(255, 243, 224, 0.2), rgba(255, 243, 224, 0) 34%),
    linear-gradient(180deg, #2f3846 0%, #171d26 100%);
}

.play-limit-card {
  width: min(100%, 380px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-align: center;
  color: #fff;
}

.play-limit-icon {
  width: 76px;
  height: 76px;
  border-radius: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffb36f;
  font-size: 34px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.18);
}

.play-limit-text {
  margin: 0;
  font-size: clamp(22px, 3.4vw, 34px);
  line-height: 1.25;
  font-weight: 900;
  letter-spacing: 0;
}

.parent-unlock-btn {
  min-width: 148px;
  min-height: 46px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(255, 107, 53, 0.28);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.parent-unlock-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 32px rgba(255, 107, 53, 0.34);
}

.parent-unlock-btn:active {
  transform: translateY(0);
}

.player-message {
  position: absolute;
  left: 50%;
  top: 45%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(15px, 1.5vw, 22px);
  z-index: 1;
}

.player-controls {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 12px;
  z-index: 2;
}

.progress-track {
  width: 100%;
  height: 3px;
  border-radius: 999px;
  background: rgba(236, 240, 244, 0.34);
  overflow: hidden;
}

.progress-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #f5f7f8;
  transition: width 0.25s linear;
}

.controls-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.94);
}

.control-btn {
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: inherit;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.control-btn:hover {
  color: #ffffff;
}

.control-btn:disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

.control-btn.active {
  color: #8fd7ff;
}

.cast-state {
  font-size: 12px;
  color: rgba(226, 236, 247, 0.92);
  font-weight: 600;
  margin-right: 2px;
}

.time-text {
  font-size: clamp(12px, 0.95vw, 16px);
  font-weight: 700;
  letter-spacing: 0.1px;
}

.controls-spacer {
  flex: 1;
}

.meta-header {
  margin-top: 26px;
}

.play-title {
  margin-top: 24px;
  font-size: clamp(24px, 4vw, 42px);
  line-height: 1.15;
  color: #13161b;
  font-weight: 800;
}

.content-section {
  display: block;
  min-width: 0;
  width: 100%;
}

.content-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.favorite-season-btn {
  flex: 0 0 auto;
  min-width: 86px;
  min-height: 44px;
  margin-top: 0;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 999px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 248, 241, 0.74) 100%);
  box-shadow:
    0 14px 30px rgba(92, 77, 64, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.86);
  color: #7a7068;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s ease,
    color 0.22s ease,
    background 0.22s ease;
}

.favorite-season-btn:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 182, 145, 0.92);
  box-shadow:
    0 18px 34px rgba(255, 138, 61, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: #e26d3f;
}

.favorite-season-btn.active {
  border-color: rgba(255, 154, 111, 0.92);
  background:
    linear-gradient(180deg, rgba(255, 241, 235, 0.96) 0%, rgba(255, 229, 217, 0.88) 100%);
  color: #e56d54;
  box-shadow:
    0 18px 34px rgba(255, 138, 61, 0.2),
    0 0 0 1px rgba(255, 154, 111, 0.18);
}

.favorite-season-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.72);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: inherit;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.88);
}

.favorite-season-btn.active .favorite-season-icon {
  background: linear-gradient(135deg, #ffb08d 0%, #ff8a6a 100%);
  color: #fff8f5;
  box-shadow: 0 10px 22px rgba(255, 138, 106, 0.28);
}

.favorite-season-text {
  font-size: 13px;
  font-weight: 800;
  line-height: 1.1;
  text-align: center;
}

.section-title {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  font-size: clamp(20px, 2.5vw, 32px);
  line-height: 1.1;
  font-weight: 800;
  color: #191b20;
}

.section-title::after {
  content: '';
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffc88e 0%, #ff9b4d 52%, #ff7534 100%);
  box-shadow: 0 5px 14px rgba(255, 123, 52, 0.28), inset 0 1px 1px rgba(255, 255, 255, 0.6);
}

.series-desc {
  margin-top: 14px;
  color: #535861;
  font-size: clamp(16px, 1.75vw, 24px);
  line-height: 1.7;
}

.season-tabs-section {
  margin-top: 28px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.season-tabs {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  padding: 14px 8px;
  margin: -14px -8px;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.season-tabs::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.season-tab {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #666;
  font-size: clamp(20px, 2.2vw, 34px);
  font-weight: 600;
  min-height: 56px;
  white-space: nowrap;
  padding: 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  flex: 0 0 auto;
}

.season-tab:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

.season-tab.active {
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.46),
    0 0 16px rgba(255, 184, 108, 0.55),
    0 0 28px rgba(255, 107, 53, 0.34);
}

.menu-toggle-btn {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #bebebe;
  background: rgba(239, 239, 239, 0.96);
  color: #26282e;
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  flex-shrink: 0;
}

.menu-toggle-btn svg {
  transition: transform 0.25s ease;
}

.menu-toggle-btn .expanded {
  transform: rotate(180deg);
}

.episode-panel {
  margin-top: 30px;
  padding: 24px 0 8px;
}

.episode-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.episode-panel-title {
  margin: 0;
  color: #272a31;
  font-size: clamp(18px, 2.2vw, 28px);
  font-weight: 800;
  line-height: 1.1;
}

.episode-count-btn {
  border: none;
  background: transparent;
  color: #98a0ab;
  font-size: clamp(14px, 1.5vw, 22px);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 0;
}

.episode-range-section {
  margin-top: 16px;
}

.episode-range-chips {
  display: flex;
  gap: 30px;
  padding: 0 0 10px;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.episode-range-chips::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.episode-range-chip {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background: transparent;
  color: #8d95a1;
  font-size: clamp(16px, 1.7vw, 24px);
  font-weight: 700;
  white-space: nowrap;
  padding: 0 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s ease;
  flex: 0 0 auto;
  position: relative;
}

.episode-range-chip:hover {
  color: #5f82c3;
}

.episode-range-chip.active {
  color: #ff7a36;
}

.episode-range-chip.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 999px;
  background: #ff7a36;
}

.episode-grid {
  margin-top: 10px;
  display: flex;
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 0 14px;
  scroll-snap-type: x proximity;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.episode-grid::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.episode-card {
  flex: 0 0 calc((100% - 48px) / 4);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.2s ease;
  scroll-snap-align: start;
}

.episode-card:hover {
  transform: translateY(-2px);
}

.episode-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 1.55 / 1;
  border-radius: 14px;
  background: linear-gradient(145deg, var(--cover-start), var(--cover-end));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.episode-card.active .episode-cover::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 2px solid #ff7a36;
  border-radius: inherit;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 2;
}

.cover-icon {
  color: #8397a3;
  font-size: 20px;
}

.episode-tag {
  position: absolute;
  right: 8px;
  bottom: 8px;
  min-width: 52px;
  padding: 4px 8px;
  border-radius: 999px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.92);
}

.episode-tag.learned {
  color: #4b9e1e;
}

.episode-tag.unlearned {
  color: #ff8a3d;
}

.episode-title {
  margin-top: 10px;
  color: #272b33;
  font-size: clamp(15px, 1.45vw, 21px);
  font-weight: 700;
  line-height: 1.28;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
}

.episode-subtitle {
  margin-top: 4px;
  color: #7e8691;
  font-size: clamp(13px, 1.2vw, 17px);
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.sheet-mask {
  position: fixed;
  inset: 0;
  z-index: 30;
  background: rgba(17, 21, 26, 0.34);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 12px 12px;
}

.sheet-panel {
  width: min(720px, 100%);
  max-height: min(78vh, 920px);
  border-radius: 24px;
  border: 1px solid rgba(221, 214, 202, 0.95);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 -12px 38px rgba(24, 25, 26, 0.16);
  padding: 10px 14px 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.sheet-title {
  margin: 0 0 12px;
  text-align: center;
  font-size: 16px;
  color: #3b3b3b;
  font-weight: 700;
}

.sheet-option {
  width: 100%;
  border: 1px solid #e6e0d8;
  border-radius: 14px;
  background: #f8f6f2;
  color: #3f3f3f;
  font-size: 15px;
  font-weight: 600;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.25s ease;
}

.sheet-option + .sheet-option {
  margin-top: 10px;
}

.sheet-option.active {
  border-color: #ffb788;
  background: linear-gradient(135deg, #ffeddc 0%, #ffe3d0 100%);
  color: #cf5c1f;
}

.sheet-check {
  font-size: 13px;
  font-weight: 700;
}

.sheet-cancel {
  width: 100%;
  margin-top: 12px;
  border: none;
  border-radius: 14px;
  background: #ece7de;
  color: #4c4c4c;
  font-size: 15px;
  font-weight: 700;
  padding: 13px 14px;
  cursor: pointer;
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.25s ease;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-fade-enter-active .sheet-panel,
.sheet-fade-leave-active .sheet-panel {
  transition: transform 0.25s ease;
}

.sheet-fade-enter-from .sheet-panel,
.sheet-fade-leave-to .sheet-panel {
  transform: translateY(100%);
}

.parent-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(52, 42, 34, 0.36);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.parent-modal {
  position: relative;
  width: min(560px, 100%);
  border-radius: 34px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(255, 251, 246, 0.98) 0%, rgba(249, 241, 231, 0.97) 100%);
  border: 1px solid rgba(255, 184, 136, 0.28);
  box-shadow:
    0 30px 60px rgba(140, 104, 68, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.parent-modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.56);
  color: #8f7b6a;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.parent-modal-close:hover {
  background: rgba(255, 255, 255, 0.84);
  color: #6b594a;
}

.parent-modal-header {
  padding: 34px 28px 22px;
  text-align: center;
  border-bottom: 1px solid rgba(186, 159, 133, 0.24);
}

.parent-modal-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.1;
  color: #3f342c;
  font-weight: 800;
}

.parent-modal-subtitle {
  margin: 10px 0 0;
  color: #8d7c6d;
  font-size: 15px;
  font-weight: 700;
}

.parent-modal-body {
  padding: 28px;
}

.parent-lookup-label {
  display: block;
  margin-bottom: 10px;
  color: #5f5044;
  font-size: 15px;
  font-weight: 800;
}

.parent-input-shell {
  min-height: 64px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(186, 159, 133, 0.28);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  padding: 0 14px 0 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.parent-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: #4f4137;
  font-size: 18px;
  outline: none;
}

.parent-input::placeholder {
  color: #ab9b8d;
}

.parent-input-side-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #a18f80;
  cursor: pointer;
}

.parent-input-side-btn:hover {
  background: rgba(255, 255, 255, 0.55);
  color: #6f6054;
}

.parent-lookup-error {
  margin: 10px 0 0;
  color: #d84f3d;
  font-size: 14px;
  font-weight: 700;
}

.parent-submit-btn {
  width: 100%;
  min-height: 70px;
  margin-top: 24px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(180deg, #e6dfd6 0%, #d8cfc4 100%);
  color: rgba(50, 42, 35, 0.72);
  font-size: 20px;
  font-weight: 800;
  cursor: not-allowed;
  box-shadow: 0 12px 24px rgba(153, 120, 87, 0.08);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease,
    color 0.25s ease;
}

.parent-submit-btn.active {
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(255, 138, 61, 0.24);
}

.parent-submit-btn.active:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 28px rgba(255, 138, 61, 0.32);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 980px) {
  .page-shell {
    width: calc(100% - 28px);
  }

  .favorite-season-btn {
    min-width: 84px;
    min-height: 42px;
    padding: 7px 11px;
  }

  .season-tab {
    min-height: 50px;
    padding: 0 28px;
    font-size: 18px;
  }

  .menu-toggle-btn {
    width: 50px;
    height: 50px;
    font-size: 18px;
  }

  .episode-card {
    flex-basis: 220px;
  }
}

@media (max-width: 768px) {
  .page-shell {
    width: calc(100% - 32px);
    padding-top: 0;
  }

  .content-section {
    width: 100%;
  }

  .favorite-season-btn {
    min-width: 80px;
    min-height: 40px;
    margin-top: 0;
    padding: 6px 10px;
    border-radius: 999px;
    gap: 5px;
  }

  .favorite-season-icon {
    width: 22px;
    height: 22px;
    font-size: 12px;
  }

  .favorite-season-text {
    font-size: 12px;
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    z-index: 20;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-topbar.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .mobile-topbar-offset {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 56px);
  }

  .mobile-back-btn {
    display: inline-flex;
    position: absolute;
    left: 16px;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
    transform: none;
  }
}

@media (max-width: 680px) {
  .play-page {
    padding-top: 10px;
  }

  .content-section {
    width: 100%;
  }

  .section-title::after {
    height: 5px;
  }

  .favorite-season-btn {
    min-width: 76px;
    min-height: 38px;
    margin-top: 0;
    padding: 6px 9px;
    gap: 5px;
  }

  .episode-panel {
    margin-top: 24px;
    padding-bottom: 8px;
  }

  .season-tabs {
    gap: 10px;
  }

  .season-tab {
    min-height: 42px;
    padding: 0 20px;
    font-size: 18px;
  }

  .menu-toggle-btn {
    width: 42px;
    height: 42px;
    font-size: 13px;
  }

  .episode-range-chips {
    gap: 18px;
  }

  .episode-range-chip {
    font-size: 18px;
  }

  .episode-grid {
    gap: 12px;
  }

  .episode-card {
    flex-basis: 176px;
  }

  .sheet-mask {
    padding: 0;
  }

  .sheet-panel {
    width: 100%;
    max-height: 82vh;
    border-radius: 22px 22px 0 0;
    border-bottom: none;
    padding: 10px 12px 16px;
  }
}

@media (max-width: 480px) {
  .page-shell {
    width: calc(100% - 28px);
  }

  .content-section {
    width: 100%;
  }

  .mobile-topbar {
    padding-left: 14px;
    padding-right: 14px;
  }

  .mobile-back-btn {
    left: 14px;
  }

  .mobile-topbar-title {
    font-size: 18px;
  }

  .favorite-season-btn {
    min-width: 72px;
    min-height: 36px;
    padding: 5px 8px;
    border-radius: 999px;
  }

  .favorite-season-icon {
    width: 21px;
    height: 21px;
    font-size: 11px;
  }

  .favorite-season-text {
    font-size: 11px;
  }
}
</style>
